import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LayoutDashboard, MessageSquare, FileText, Wrench, BarChart3,
  Key, Receipt, Settings, Users, Hash, Cpu, Command, LogOut,
  ChevronLeft, ChevronRight, Bell, Search, Sparkles,
} from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { BRAND, AGENTS } from '../../lib/brand';
import { CognaraLogo } from './CognaraLogo';
import { CognaraWordmark } from './CognaraWordmark';
import { ThemeToggle } from '../nexus-ui/ThemeToggle';
import { NotificationBell } from '../notifications/NotificationBell';

/**
 * Instrument-Grade Shell
 * ----------------------
 * Dual-layer chrome used by both Admin and Portal:
 *   • TopBar: breadcrumb + ⌘K trigger + notifications + user
 *   • SideNav: collapsible (64px ↔ 240px), icon-first, with
 *              live status widget at the bottom showing active agent.
 *
 * Thin layer over existing Outlet — no data fetching inside.
 */

export const SideNav = ({ items, scope, userName, userEmail, onLogout, isSuperAdmin }) => {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const v = localStorage.getItem('cognara.sidenav.collapsed') ?? localStorage.getItem('sutradhar.sidenav.collapsed') ?? localStorage.getItem('capexara.sidenav.collapsed');
      return v === '1';
    } catch { return false; }
  });
  const location = useLocation();

  const toggle = useCallback(() => {
    setCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem('cognara.sidenav.collapsed', next ? '1' : '0'); } catch {}
      return next;
    });
  }, []);

  const widthCls = collapsed ? 'w-[64px]' : 'w-[240px]';

  return (
    <motion.aside
      animate={{ width: collapsed ? 64 : 240 }}
      transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
      className={`relative h-screen flex flex-col border-r flex-shrink-0 ${widthCls}`}
      style={{ background: 'var(--layer-1)', borderColor: 'var(--hairline)' }}
      data-testid={`sidenav-${scope}`}
    >
      {/* Brand */}
      <div className="h-[64px] flex items-center gap-2.5 px-4 border-b" style={{ borderColor: 'var(--hairline)' }}>
        <CognaraLogo variant="icon" size={30} style={{ color: 'var(--nexus-text-primary)' }} />
        {!collapsed && (
          <div className="min-w-0">
            <CognaraWordmark size={18} color="var(--nexus-text-primary)" />
            {scope === 'admin' && (
              <div className="ig-eyebrow text-[9px] truncate mt-0.5" style={{ color: 'var(--nexus-text-tertiary)' }}>
                control plane
              </div>
            )}
          </div>
        )}
      </div>

      {/* Nav items */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {items.map((item) => {
          const Icon = item.icon;
          const active = location.pathname === item.to || location.pathname.startsWith(item.to + '/');
          return (
            <Link
              key={item.to}
              to={item.to}
              aria-current={active ? 'page' : undefined}
              aria-label={collapsed ? item.label : undefined}
              className={`group relative flex items-center gap-2.5 h-9 rounded-lg text-[13px] font-medium transition-all overflow-hidden
                ${collapsed ? 'px-2.5 justify-center' : 'px-2.5'}
                ${active
                  ? 'text-nexus-accent-mint'
                  : 'text-nexus-text-secondary hover:text-nexus-text-primary hover:bg-white/[0.04]'}
              `}
              style={active ? { background: 'rgba(30, 78, 140, 0.10)' } : {}}
              data-testid={`sidenav-${item.key || item.label.toLowerCase().replace(/\s/g, '-')}`}
              title={collapsed ? item.label : ''}
            >
              {active && (
                <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-0.5 rounded-r-full" style={{ background: 'var(--nexus-accent-blue)' }} />
              )}
              <Icon size={16} className="flex-shrink-0" />
              {!collapsed && (
                <span className="truncate transition-transform group-hover:translate-x-[2px]">{item.label}</span>
              )}
              {!collapsed && item.badge && (
                <span className="ml-auto ig-mono text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(242, 107, 31, 0.15)', color: '#F26B1F' }}>
                  {item.badge}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Live agent status widget */}
      <div className="px-2 py-2 border-t" style={{ borderColor: 'var(--hairline)' }}>
        {collapsed ? (
          <div className="w-10 h-10 mx-auto rounded-lg flex items-center justify-center"
               style={{ background: 'rgba(30, 78, 140, 0.12)', border: '1px solid rgba(30, 78, 140, 0.25)' }}
               title={`${AGENTS.sutradhar.display} — Online`}>
            <span className="ig-live-dot" />
          </div>
        ) : (
          <div className="rounded-lg p-2.5" style={{ background: 'rgba(30, 78, 140, 0.06)', border: '1px solid rgba(30, 78, 140, 0.18)' }}>
            <div className="flex items-center gap-2 mb-1">
              <span className="ig-live-dot" />
              <span className="ig-eyebrow text-[9px]" style={{ color: 'var(--nexus-accent-blue)' }}>agent online</span>
            </div>
            <div className="text-[12px] font-medium text-nexus-text-primary leading-tight">
              {AGENTS.sutradhar.display}
            </div>
            <div className="ig-mono text-[10px] text-nexus-text-tertiary">{AGENTS.sutradhar.role}</div>
          </div>
        )}
      </div>

      {/* Footer: user + collapse */}
      <div className="px-2 py-2 border-t flex items-center gap-2" style={{ borderColor: 'var(--hairline)' }}>
        {!collapsed && (
          <div className="flex-1 min-w-0 px-1.5">
            <div className="flex items-center gap-1.5 min-w-0">
              <div className="text-[12px] font-medium text-nexus-text-primary truncate">{userName || 'User'}</div>
              {isSuperAdmin && (
                <span className="inline-flex items-center px-1 py-0.5 rounded text-[8.5px] font-semibold uppercase tracking-wider flex-shrink-0"
                      style={{
                        background: 'rgba(167,139,250,0.15)',
                        color: '#C4B5FD',
                        border: '1px solid rgba(167,139,250,0.30)',
                      }}
                      title="Super-admin · unlimited"
                      data-testid="sidenav-super-admin-pill">
                  SUPER
                </span>
              )}
            </div>
            <div className="ig-mono text-[10px] text-nexus-text-tertiary truncate">{userEmail}</div>
          </div>
        )}
        {onLogout && (
          <button
            onClick={onLogout}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-nexus-text-tertiary hover:text-nexus-error hover:bg-white/[0.04] transition-colors"
            title="Sign out"
            aria-label="Sign out"
            data-testid="sidenav-logout"
          >
            <LogOut size={14} />
          </button>
        )}
        <button
          onClick={toggle}
          className="w-8 h-8 rounded-lg flex items-center justify-center text-nexus-text-tertiary hover:text-nexus-text-primary hover:bg-white/[0.04] transition-colors"
          title={collapsed ? 'Expand' : 'Collapse'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
          data-testid="sidenav-toggle"
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </div>
    </motion.aside>
  );
};

/**
 * TopBar — breadcrumb + ⌘K + notifications + avatar.
 * Dispatches a custom event 'cognara.commandbar.open' the global
 * CommandBar listens for (complementing its built-in ⌘K hotkey).
 */
export const TopBar = ({ title, crumbs = [], rightSlot }) => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  const triggerCmdK = () => {
    window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }));
  };

  return (
    <div className="h-[64px] flex items-center gap-3 px-5 border-b flex-shrink-0"
         style={{ background: 'var(--layer-1)', borderColor: 'var(--hairline)' }}>
      <div className="flex items-center gap-2 min-w-0">
        {crumbs.map((c, i) => (
          <span key={i} className="flex items-center gap-2">
            {i > 0 && <span className="text-nexus-text-tertiary text-xs">/</span>}
            <span className={`text-[13px] ${i === crumbs.length - 1 ? 'text-nexus-text-primary font-medium' : 'text-nexus-text-tertiary'}`}>
              {c}
            </span>
          </span>
        ))}
        {title && crumbs.length === 0 && (
          <span className="text-[14px] font-medium text-nexus-text-primary">{title}</span>
        )}
      </div>

      <div className="flex-1" />

      {/* Timestamp */}
      <span className="ig-mono text-[10px] text-nexus-text-tertiary hidden md:inline-flex items-center gap-1.5">
        <span className="ig-live-dot" style={{ width: 6, height: 6 }} />
        {now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </span>

      {/* ⌘K trigger */}
      <button
        onClick={triggerCmdK}
        className="group flex items-center gap-2 h-8 px-2.5 rounded-lg text-[12px] transition-colors"
        style={{
          background: 'var(--layer-2)',
          border: '1px solid var(--hairline)',
          color: 'var(--nexus-text-tertiary)',
        }}
        data-testid="topbar-cmdk"
        title="Command Bar (⌘K)"
        aria-label="Open command palette (⌘K)"
        aria-keyshortcuts="Meta+K Control+K"
      >
        <Search size={12} />
        <span className="hidden sm:inline">Search…</span>
        <kbd className="ig-mono text-[9px] px-1 py-0.5 rounded border" style={{ borderColor: 'var(--hairline)' }}>⌘K</kbd>
      </button>

      {/* Notifications */}
      <NotificationBell />

      {/* Theme toggle */}
      <ThemeToggle size="sm" />

      {rightSlot}
    </div>
  );
};

export default { SideNav, TopBar };
